import { RealtimeTranscriptionErrorCode } from 'chaika-enums';

import { MicrophoneUnavailableError } from '../MicrophoneUnavailableError';
import { PcmCapture } from '../PcmCapture';
import { RealtimeTranscriptionClient } from '../RealtimeTranscriptionClient';
import { DictationErrorKind, DictationFieldBinding, DictationState } from '../types';

import { ERROR_NOTICE_MS, FAILED_UTTERANCE_NOTICE_MS } from './config';
import { DictationSession } from './types';

const IDLE_STATE: Omit<DictationState, 'error' | 'failedUtteranceFieldId'> = {
    activeFieldId: null,
    phase: 'idle',
};

/** Owns the single dictation session (mic, socket, text assembly); starting another field stops the previous one. */
export class DictationController {
    private state: DictationState = { ...IDLE_STATE, error: null, failedUtteranceFieldId: null };
    private readonly listeners = new Set<() => void>();
    private session: DictationSession | null = null;
    // invalidates in-flight start() continuations while this.session is not yet assigned (mic prompt pending)
    private startEpoch = 0;
    private failedUtteranceTimer: ReturnType<typeof setTimeout> | null = null;
    private errorTimer: ReturnType<typeof setTimeout> | null = null;

    public subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);

        return () => this.listeners.delete(listener);
    };

    public getState = (): DictationState => this.state;

    public async toggle(binding: DictationFieldBinding): Promise<void> {
        // checked against state, not this.session: during the mic prompt there is no session yet
        if (this.state.activeFieldId === binding.fieldId) {
            await this.stopSession();

            return;
        }
        await this.start(binding);
    }

    public async stopIfActive(fieldId: string): Promise<void> {
        if (this.state.activeFieldId === fieldId) {
            await this.stopSession();
        }
    }

    private async start(binding: DictationFieldBinding): Promise<void> {
        await this.stopSession();
        const epoch = ++this.startEpoch;

        this.setState({
            ...IDLE_STATE,
            activeFieldId: binding.fieldId,
            phase: 'starting',
            error: null,
            failedUtteranceFieldId: null,
        });

        let stream: MediaStream;
        try {
            stream = await PcmCapture.requestMicrophone();
        } catch (error) {
            if (epoch === this.startEpoch) {
                const kind =
                    error instanceof MicrophoneUnavailableError
                        ? DictationErrorKind.Microphone
                        : DictationErrorKind.StartFailed;
                this.failStart(binding.fieldId, kind, error);
            }

            return;
        }

        if (epoch !== this.startEpoch) {
            // cancelled (or replaced by another field) while the permission prompt was open
            stream.getTracks().forEach(track => track.stop());

            return;
        }

        const session: DictationSession = {
            binding,
            client: new RealtimeTranscriptionClient({
                onPartial: (utteranceId, text) => this.applyUtterance(session, utteranceId, text),
                onCommitted: (utteranceId, text) => this.applyUtterance(session, utteranceId, text),
                onUtteranceFailed: utteranceId => this.dropUtterance(session, utteranceId),
                onClosed: errorCode => this.handleServerClose(session, errorCode),
            }),
            stream,
            capture: null,
            baseText: binding.getText(),
            utteranceOrder: [],
            utteranceTexts: new Map(),
        };
        this.session = session;

        let sampleRate: number;
        try {
            ({ sampleRate } = await session.client.connect());
        } catch (error) {
            if (this.session === session) {
                this.session = null;
                stream.getTracks().forEach(track => track.stop());
                this.failStart(binding.fieldId, DictationErrorKind.StartFailed, error);
            }

            return;
        }

        if (this.session !== session) {
            // stopped (or replaced by another field) while connecting
            session.client.close();
            stream.getTracks().forEach(track => track.stop());

            return;
        }

        session.capture = new PcmCapture(stream, frame => session.client.sendAudio(frame));
        try {
            await session.capture.start(sampleRate);
        } catch (error) {
            if (this.session === session) {
                await this.teardownSession(session);
                this.failStart(binding.fieldId, DictationErrorKind.StartFailed, error);
            }

            return;
        }

        if (this.session !== session) {
            await this.teardownSession(session);

            return;
        }

        this.setState({ ...this.state, phase: 'active' });
    }

    private async stopSession(): Promise<void> {
        this.startEpoch++;
        const session = this.session;
        this.session = null;
        if (session) {
            await this.teardownSession(session);
        }
        if (this.state.phase !== 'idle') {
            this.setState({ ...IDLE_STATE, error: this.state.error, failedUtteranceFieldId: null });
        }
    }

    private async teardownSession(session: DictationSession): Promise<void> {
        session.client.close();
        if (session.capture) {
            await session.capture.stop();
        } else {
            session.stream?.getTracks().forEach(track => track.stop());
        }
    }

    private handleServerClose(session: DictationSession, errorCode: RealtimeTranscriptionErrorCode | null): void {
        if (this.session !== session) {
            return;
        }
        this.session = null;
        void this.teardownSession(session);

        // reaching the 10-min cap is a normal end of dictation, not a failure
        if (errorCode === RealtimeTranscriptionErrorCode.SessionExpired) {
            this.setState({ ...IDLE_STATE, error: null, failedUtteranceFieldId: null });

            return;
        }

        // silence stop is also a normal end, but the user must learn why the mic went off
        if (errorCode === RealtimeTranscriptionErrorCode.IdleTimeout) {
            this.showError(session.binding.fieldId, DictationErrorKind.IdleTimeout);

            return;
        }

        console.error(`[Dictation] session ended by server: ${errorCode ?? 'connection lost'}`);
        this.showError(session.binding.fieldId, DictationErrorKind.Interrupted);
    }

    private applyUtterance(session: DictationSession, utteranceId: string, text: string): void {
        if (this.session !== session) {
            return;
        }
        if (!session.utteranceTexts.has(utteranceId)) {
            session.utteranceOrder.push(utteranceId);
        }
        session.utteranceTexts.set(utteranceId, text);
        session.binding.setText(this.assembleText(session));
    }

    private dropUtterance(session: DictationSession, utteranceId: string): void {
        if (this.session !== session || !session.utteranceTexts.has(utteranceId)) {
            return;
        }
        session.utteranceTexts.delete(utteranceId);
        session.utteranceOrder = session.utteranceOrder.filter(id => id !== utteranceId);
        session.binding.setText(this.assembleText(session));
        this.showFailedUtteranceNotice(session.binding.fieldId);
    }

    private assembleText(session: DictationSession): string {
        const utterances = session.utteranceOrder.map(id => session.utteranceTexts.get(id)?.trim()).filter(Boolean);
        const dictated = utterances.join(' ');

        if (!session.baseText) {
            return dictated;
        }
        if (!dictated) {
            return session.baseText;
        }
        const separator = /\s$/.test(session.baseText) ? '' : ' ';

        return `${session.baseText}${separator}${dictated}`;
    }

    private showFailedUtteranceNotice(fieldId: string): void {
        if (this.failedUtteranceTimer) {
            clearTimeout(this.failedUtteranceTimer);
        }
        this.setState({ ...this.state, failedUtteranceFieldId: fieldId });
        this.failedUtteranceTimer = setTimeout(() => {
            this.failedUtteranceTimer = null;
            this.setState({ ...this.state, failedUtteranceFieldId: null });
        }, FAILED_UTTERANCE_NOTICE_MS);
    }

    private failStart(fieldId: string, kind: DictationErrorKind, error: unknown): void {
        console.error(`[Dictation] failed to start: ${(error as Error)?.message ?? error}`);
        this.showError(fieldId, kind);
    }

    private showError(fieldId: string, kind: DictationErrorKind): void {
        if (this.errorTimer) {
            clearTimeout(this.errorTimer);
        }
        this.setState({ ...IDLE_STATE, error: { fieldId, kind }, failedUtteranceFieldId: null });
        this.errorTimer = setTimeout(() => {
            this.errorTimer = null;
            this.setState({ ...this.state, error: null });
        }, ERROR_NOTICE_MS);
    }

    private setState(state: DictationState): void {
        this.state = state;
        this.listeners.forEach(listener => listener());
    }
}

export const dictationController = new DictationController();
